import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../../core/auth/AuthContext";
import { consumePendingAuthNotice } from "../../core/auth/pendingAuthNotice";
import { usePanelNotice } from "../../components/panelNotice/hooks/usePanelNotice";
import { useKeyboardAwareOffset } from "../../hooks/useKeyboardAwareOffset";
import { useModalAccessibility } from "../../hooks/useModalAccessibility";
import {
  PROFILE_TYPE_SELECTION_OPTIONS,
  normalizeProfileType,
} from "./services/profile.identity";
import ProfileIdentityChoiceIcon from "./components/ProfileIdentityChoiceIcon";
import MobileNumberWheelPicker from "../tripCost/MobileNumberWheelPicker";
import { updateUserProfile } from "./services/profile.repository";
import {
  buildPublicUsernameSuggestion,
  getPublicUsername,
  isUnsafePublicIdentityLabel,
} from "../../utils/publicIdentity";
import styles from "./ProfileCompletionGate.module.css";
import { isProfileCompletionRequired } from "./profileCompletion.model";

const USERNAME_MAX_LENGTH = 32;
const CURRENT_YEAR = new Date().getFullYear();
const MIN_AGE = 18;
const MAX_AGE = CURRENT_YEAR - 1940;

function normalizeUsername(value) {
  return String(value || "")
    .trim()
    .replace(/^@+/, "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9._-]+/g, ".")
    .replace(/[.]{2,}/g, ".")
    .replace(/^[._-]+|[._-]+$/g, "")
    .slice(0, USERNAME_MAX_LENGTH);
}

function ChoiceGroup({
  label,
  value,
  options,
  onChange,
  disabled = false,
}) {
  return (
    <fieldset className={styles.choiceGroup}>
      <legend className={styles.choiceLegend}>{label}</legend>
      <div className={styles.choiceGrid}>
        {options.map((option) => {
          const isSelected = value === option.value;

          return (
            <button
              key={option.value}
              type="button"
              className={`${styles.choiceButton} ${
                isSelected ? styles.choiceButtonSelected : ""
              }`}
              data-profile-type={option.value}
              onClick={() => onChange(option.value)}
              disabled={disabled}
              aria-pressed={isSelected}
            >
              <span className={styles.choiceState} aria-hidden="true">
                {isSelected ? (
                  <svg viewBox="0 0 16 16" fill="none">
                    <path d="m4 8.2 2.35 2.35L12.2 4.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : null}
              </span>
              <span className={styles.choiceIcon} aria-hidden="true">
                <ProfileIdentityChoiceIcon icon={option.icon} />
              </span>
              <span className={styles.choiceCopy}>
                <span className={styles.choiceLabel}>{option.label}</span>
                {option.supportingLabel ? (
                  <span className={styles.choiceSupportingLabel}>
                    {option.supportingLabel}
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function AgeField({ id, value, onChange, onOpenPicker, disabled }) {
  return (
    <div className={styles.ageField}>
      <span className={styles.ageInputShell}>
        <input
          id={id}
          aria-label="Ton âge"
          className={styles.ageInput}
          type="number"
          inputMode="numeric"
          min={MIN_AGE}
          max={MAX_AGE}
          step="1"
          value={value}
          onChange={(event) => onChange(event.target.value.replace(/\D/g, "").slice(0, 2))}
          disabled={disabled}
          required
        />
        <span className={styles.ageUnit}>ans</span>
        <button
          type="button"
          className={styles.ageWheelTrigger}
          onClick={onOpenPicker}
          disabled={disabled}
          aria-label={value ? `Âge sélectionné : ${value} ans. Modifier` : "Choisir un âge"}
        >
          <span className={value ? styles.ageWheelValue : styles.ageWheelPlaceholder}>
            {value || "Choisir"}
          </span>
          <span className={styles.ageWheelUnit}>{value ? "ans" : "Faire tourner la roue"}</span>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="m8 9 4-4 4 4M8 15l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </span>
    </div>
  );
}

export default function ProfileCompletionGate() {
  const keyboardOffset = useKeyboardAwareOffset();
  const dialogRef = useRef(null);
  const { showNotice } = usePanelNotice();
  const {
    user,
    loading,
    profile,
    isAuthenticated,
    refreshProfile,
    logout,
  } = useAuth();
  const [usernameDraft, setUsernameDraft] = useState("");
  const [profileTypeDraft, setProfileTypeDraft] = useState("");
  const [ageDraft, setAgeDraft] = useState("");
  const [agePickerOpen, setAgePickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [error, setError] = useState("");
  const [completedUserId, setCompletedUserId] = useState("");
  const currentProfileType = useMemo(
    () => normalizeProfileType(user?.profile?.profileType || user?.profileType),
    [user?.profile?.profileType, user?.profileType]
  );
  const currentUsername = useMemo(
    () => getPublicUsername({ username: normalizeUsername(user?.username || "") }),
    [user?.username]
  );
  const suggestedUsername = useMemo(
    () => buildPublicUsernameSuggestion(user, USERNAME_MAX_LENGTH),
    [user]
  );
  const identityIsLocked = Boolean(user?.profile?.identityLockedAt);
  const normalizedUsernameDraft = useMemo(
    () => normalizeUsername(usernameDraft),
    [usernameDraft]
  );
  const usernameError =
    normalizedUsernameDraft.length >= 3 &&
    isUnsafePublicIdentityLabel(normalizedUsernameDraft)
      ? "Choisis un pseudo qui ne ressemble pas a une adresse email."
      : normalizedUsernameDraft.length < 3
      ? "Choisis un pseudo de 3 caractères minimum."
      : "";

  const shouldAskForIdentity = Boolean(
    !loading &&
      profile !== null &&
      user?.uid &&
      !user?.isAnonymous &&
      completedUserId !== user.uid &&
      isProfileCompletionRequired(user)
  );
  const shouldShowRecovery = Boolean(
    !loading && isAuthenticated && !user?.isAnonymous && profile === null
  );
  useModalAccessibility(dialogRef, {
    active: (shouldAskForIdentity || shouldShowRecovery) && !agePickerOpen,
  });

  useEffect(() => {
    if (
      loading ||
      !profile ||
      !user?.uid ||
      isProfileCompletionRequired(user)
    ) {
      return;
    }

    const pendingNotice = consumePendingAuthNotice();
    if (pendingNotice) showNotice(pendingNotice);
  }, [loading, profile, showNotice, user]);

  useEffect(() => {
    setUsernameDraft(currentUsername || suggestedUsername);
    setProfileTypeDraft(currentProfileType);
    setAgeDraft(
      user?.profile?.age !== null &&
      user?.profile?.age !== undefined &&
      Number.isInteger(Number(user.profile.age))
        ? String(Number(user.profile.age))
        : user?.profile?.birthYear
        ? String(CURRENT_YEAR - Number(user.profile.birthYear))
        : ""
    );
    setError("");
  }, [currentProfileType, currentUsername, suggestedUsername, user?.profile?.age, user?.profile?.birthYear, user?.uid]);

  const age = Number(ageDraft);
  const ageIsValid = Number.isInteger(age) && age >= MIN_AGE && age <= MAX_AGE;

  const canSubmit = Boolean(
    normalizedUsernameDraft &&
      !usernameError &&
      (identityIsLocked || profileTypeDraft) &&
      ageIsValid &&
      !saving
  );
  const completionHint = usernameError
    ? usernameError
    : !identityIsLocked && !profileTypeDraft
    ? "Choisis Homme, Femme ou Couple."
    : !ageIsValid
    ? "Choisis ton âge pour continuer."
    : "Ton profil est prêt.";

  const closeAgePicker = useCallback(() => setAgePickerOpen(false), []);
  const confirmAgePicker = useCallback((nextAge) => {
    setAgeDraft(String(nextAge));
    setAgePickerOpen(false);
    setError("");
  }, []);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (!user?.uid || !canSubmit) {
        setError("Complète ton pseudo, ton profil et ton âge pour continuer.");
        return;
      }

      setSaving(true);
      setError("");

      try {
        const profilePatch = {};

        if (!identityIsLocked) {
          profilePatch["profile.profileType"] = profileTypeDraft;
          profilePatch["profile.identityLockedAt"] = new Date().toISOString();
        }

        profilePatch["profile.age"] = age;
        profilePatch["profile.birthYear"] = CURRENT_YEAR - age;

        if (normalizedUsernameDraft !== currentUsername) {
          profilePatch.username = normalizedUsernameDraft;
        }

        await updateUserProfile(user.uid, profilePatch);
        setCompletedUserId(user.uid);
      } catch (saveError) {
        console.error(saveError);
        if (saveError?.code === "profile/conflict") {
          const uidSuffix = String(user.uid).replace(/[^a-z0-9]/gi, "").slice(-4).toLowerCase();
          const suggestedAlternative = normalizeUsername(
            `${normalizedUsernameDraft}.${uidSuffix || "sorties"}`
          );
          setUsernameDraft(suggestedAlternative);
        }
        // Les codes profile/* portent deja un message francais actionnable
        // (age-locked, identity-locked, pseudo invalide...) : l'afficher
        // plutot que le masquer derriere un message generique qui ne dit
        // jamais pourquoi l'enregistrement a echoue.
        setError(
          saveError?.code === "profile/conflict"
            ? "Ce pseudo est déjà utilisé. Une autre proposition est prête."
            : typeof saveError?.code === "string" && saveError.code.startsWith("profile/") && saveError?.message
            ? saveError.message
            : "Impossible d'enregistrer ton profil pour le moment."
        );
      } finally {
        setSaving(false);
      }
    },
    [
      canSubmit,
      currentUsername,
      identityIsLocked,
      age,
      normalizedUsernameDraft,
      profileTypeDraft,
      user?.uid,
    ]
  );

  const handleRecovery = useCallback(async () => {
    setRecovering(true);
    setError("");
    const result = await refreshProfile();
    if (!result?.success) {
      setError("Impossible de préparer ton profil. Vérifie ta connexion puis réessaie.");
    }
    setRecovering(false);
  }, [refreshProfile]);

  if (shouldShowRecovery) {
    return (
      <div className={styles.overlay}>
        <section
          ref={dialogRef}
          className={`${styles.modal} ${styles.recoveryModal}`}
          role="alertdialog"
          tabIndex={-1}
          aria-modal="true"
          aria-labelledby="profile-recovery-title"
        >
          <div className={styles.header}>
            <span className={styles.eyebrow}>Connexion interrompue</span>
            <h2 id="profile-recovery-title" className={styles.title}>
              Préparons ton profil
            </h2>
            <p className={styles.body}>
              Karango n’a pas réussi à charger ton profil. Tes données de connexion sont conservées.
            </p>
          </div>
          {error ? <div className={styles.errorText} role="alert">{error}</div> : null}
          <button type="button" className={styles.primaryButton} onClick={handleRecovery} disabled={recovering}>
            {recovering ? "Nouvelle tentative…" : "Réessayer"}
          </button>
          <button type="button" className={styles.secondaryButton} onClick={logout} disabled={recovering}>
            Se déconnecter
          </button>
        </section>
      </div>
    );
  }

  if (!shouldAskForIdentity) {
    return null;
  }

  return (
    <div className={styles.overlay} style={{ bottom: keyboardOffset }}>
      <form
        ref={dialogRef}
        className={styles.modal}
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby="profile-completion-title"
        onSubmit={handleSubmit}
      >
        <div className={styles.header}>
          <span className={styles.eyebrow}>Bienvenue</span>
          <h2 id="profile-completion-title" className={styles.title}>
            Finalise ton profil
          </h2>
          <p className={styles.body}>
            Choisis ton pseudo et comment tu participes.
          </p>
        </div>

        <label className={styles.usernameField}>
          <span className={styles.usernameLabel}>Pseudo public</span>
          <span className={styles.usernameInputShell}>
            <span className={styles.usernamePrefix} aria-hidden="true">@</span>
            <input
              type="text"
              className={styles.usernameInput}
              value={usernameDraft}
              onChange={(event) => setUsernameDraft(normalizeUsername(event.target.value))}
              maxLength={USERNAME_MAX_LENGTH}
              autoComplete="username"
              disabled={saving}
              aria-describedby="profile-completion-username-help"
            />
          </span>
          <span id="profile-completion-username-help" className={styles.usernameHelp}>
            {usernameError ||
              "Visible par les autres membres. Modifiable plus tard."}
          </span>
        </label>

        <ChoiceGroup
          label="Je participe en tant que"
          value={profileTypeDraft}
          options={PROFILE_TYPE_SELECTION_OPTIONS}
          onChange={setProfileTypeDraft}
          disabled={saving || identityIsLocked}
        />

        {!identityIsLocked ? (
          <p className={styles.identityNote}>Ce choix ne pourra plus être modifié.</p>
        ) : null}

        <fieldset className={styles.ageGroup}>
          <legend className={styles.ageLegend}>Ton âge</legend>
          <div className={styles.ageGrid}>
            <AgeField
              id="profile-completion-age"
              value={ageDraft}
              onChange={setAgeDraft}
              onOpenPicker={() => setAgePickerOpen(true)}
              disabled={saving}
            />
          </div>
          <p className={styles.ageHelp}>Karango est réservé aux personnes de 18 ans et plus.</p>
        </fieldset>

        {error ? (
          <div className={styles.errorText} role="alert">
            {error}
          </div>
        ) : null}

        <p className={styles.completionHint} role="status">
          {completionHint}
        </p>

        <button
          type="submit"
          className={styles.primaryButton}
          disabled={!canSubmit}
        >
          {saving ? "Enregistrement..." : "Continuer"}
        </button>

        {/* Sortie toujours possible : une faute de frappe dans l'adresse
            creait un compte dont on ne pouvait plus sortir (retour terrain). */}
        <p className={styles.accountSwitch}>
          {user?.email ? (
            <>
              Connecté avec <strong>{user.email}</strong>.{" "}
            </>
          ) : null}
          <button type="button" className={styles.accountSwitchButton} onClick={logout} disabled={saving}>
            {user?.email ? "Ce n'est pas toi ? Changer de compte" : "Changer de compte"}
          </button>
        </p>
      </form>

      <MobileNumberWheelPicker
        open={agePickerOpen}
        title="Ton âge"
        value={ageDraft || MIN_AGE}
        min={MIN_AGE}
        max={MAX_AGE}
        step={1}
        unit="ans"
        onCancel={closeAgePicker}
        onConfirm={confirmAgePicker}
      />
    </div>
  );
}
